/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright (c) 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { FF_APP_CANAL_PRO, FF_APP_CHAVES_NA_MAO, FF_APP_OLX, FF_APP_PORTAL_FEEDS } from '@/shared/utils/feature-flag';
import { CANAL_PRO_PORTALS, CanalProBlock, canalProEnabled, canalProFeedUrl } from './PortalSettings/CanalProBlock';
import { CNM_PORTAL, ChavesNaMaoBlock, cnmEnabled } from './PortalSettings/ChavesNaMaoBlock';
import { storedDocument, type AgencyDocument } from './ProfileSection/registration-document';
import { BlockMid, SettingsBlock } from './blocks';
import { getPortalDescriptions, getPortalLabels, olxAcceptancePending } from './PortalSettings/constants';
import type { PortalSettings } from './PortalSettings/constants';
import { SavingBar, SettingsLoadError } from './PortalSettings/widgets';
import { PortalSetupModal } from './PortalSettings/PortalSetupModal';
import { OlxConnectionBlock } from './PortalSettings/OlxConnectionBlock';
import { OlxConfirmAccountModal } from './PortalSettings/OlxConfirmAccountModal';
import { PortalsConnectIntro, noPortalConnected } from './PortalSettings/PortalsConnectIntro';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { FeedPortalsBlock, type FeedPortalRow } from './PortalSettings/FeedPortalsBlock';
import { usePortalSettingsContext } from '../hooks/portal-settings-context';
import { useOlxOAuthResult } from './PortalSettings/useOlxOAuthResult';

const FEED_EMAILS: Record<string, string> = {
  zap: 'integracao@grupozap.com',
  vivareal: 'suporte@vivareal.com.br',
  chavesnamao: 'atendimento@chavesnamao.com.br',
};

/**
 * Adds *id* to the list, or removes it when already there.
 *
 * @param list - The current membership list.
 * @param id - The portal id being switched.
 * @returns The next list.
 */
function toggleMembership(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

/**
 * The feed portals' rows, from the settings.
 *
 * @param settings - The agency's portal settings.
 * @param t - Translation function.
 * @returns One row per XML-feed portal.
 */
function buildFeedRows(settings: PortalSettings, t: (k: string) => string): FeedPortalRow[] {
  const descriptions = getPortalDescriptions(t);
  return getPortalLabels(t)
    // Chaves na Mão has its own card when its flag is on.
    .filter((portal) => portal.method !== 'oauth' && !(FF_APP_CHAVES_NA_MAO && portal.id === CNM_PORTAL))
    .map((portal) => ({
      id: portal.id,
      label: portal.label,
      description: descriptions[portal.id],
      connected: settings.connected_portals.includes(portal.id),
      enabled: settings.enabled_portals.includes(portal.id),
      autoPublish: settings.auto_publish_portals.includes(portal.id),
      lastReadAt: portal.id === 'chavesnamao' ? settings.last_cnm_read_at : settings.last_vrsync_read_at,
    }));
}

/**
 * Portais settings: the OLX card and the XML-feed portals card, drawn with
 * the same blocks and rows as the Facebook & Instagram settings.
 *
 * @returns The section.
 */
export const PortalSettingsSection: React.FC = () => {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((s) => s);
  const [modalOpen, setModalOpen] = useState<string | null>(null);
  // Which portal's settings modal is open; connecting a portal opens its own.
  const [configFor, setConfigFor] = useState<'canal-pro' | 'chaves-na-mao' | null>(null);

  const agencyId = agency?.id;
  const {
    settings, loading, saving, loadFailed, updateSettings, connectOlx, disconnectOlx, confirmOlx, checkOlxPlan,
    createCnmFeedUrl, saveCnmLeadCredentials, fetchSettings,
  } = usePortalSettingsContext();
  const justConnected = useOlxOAuthResult(agencyId, t, fetchSettings);
  // Opens by itself right after OLX's sign-in; afterwards only from the card.
  const [confirmOpen, setConfirmOpen] = useState(justConnected);

  if (loading) {
    return <div className="text-sm text-(--text-tertiary)">{t('portal.loading')}</div>;
  }
  if (!settings) {
    // Never fall back to "nothing connected": connect buttons over unknown
    // settings would overwrite the real ones.
    return loadFailed ? <SettingsLoadError onRetry={fetchSettings} t={t} /> : null;
  }

  const agencySlug = agencyId ? agencyId.toLowerCase().replace('_', '-') : '';
  const feedUrl = (portalId: string) => (
    `${window.location.origin}/api/portal-integration/feeds/${portalId === 'chavesnamao' ? 'cnm' : 'vrsync'}/${agencySlug}.xml`
  );
  const toggleEnabled = (portalId: string) => updateSettings({
    enabled_portals: toggleMembership(settings.enabled_portals, portalId),
  });
  const toggleAutoPublish = (portalId: string) => updateSettings({
    auto_publish_portals: toggleMembership(settings.auto_publish_portals, portalId),
  });
  const feedRows = FF_APP_PORTAL_FEEDS ? buildFeedRows(settings, t) : [];
  const toggleCanalPro = () => {
    const on = canalProEnabled(settings);
    const others = settings.enabled_portals.filter((p) => !(CANAL_PRO_PORTALS as readonly string[]).includes(p));
    void updateSettings({ enabled_portals: on ? others : [...others, ...CANAL_PRO_PORTALS] });
    if (!on) setConfigFor('canal-pro');
  };
  const configProps = (portal: 'canal-pro' | 'chaves-na-mao') => ({
    configOpen: configFor === portal,
    onConfigOpenChange: (open: boolean) => setConfigFor(open ? portal : null),
  });
  const withCnm = FF_APP_CHAVES_NA_MAO && !!agencyId;
  const connectCnm = () => {
    const on = cnmEnabled(settings);
    void toggleEnabled(CNM_PORTAL);
    if (!on) setConfigFor('chaves-na-mao');
  };
  // Nothing connected yet: one card with every way in, as on Facebook & Instagram.
  const showIntro = FF_APP_OLX && FF_APP_CANAL_PRO && !!agencyId
    && noPortalConnected(settings, canalProEnabled(settings), withCnm && cnmEnabled(settings));

  return (
    <div className="flex flex-col gap-(--space-4)">
      <SavingBar visible={saving} />

      {showIntro && (
        <SettingsBlock testId="portals-intro-block">
          <BlockMid>
            <PortalsConnectIntro
              saving={saving}
              onConnectCanalPro={toggleCanalPro}
              onConnectOlx={connectOlx}
              onConnectChavesNaMao={withCnm ? connectCnm : undefined}
              t={t}
            />
          </BlockMid>
        </SettingsBlock>
      )}

      {/* At least one portal on: OLX and Canal Pro share one card, and the one
          not connected yet is a single row with its connect button. */}
      {!showIntro && (FF_APP_OLX || (FF_APP_CANAL_PRO && agencyId) || withCnm) && (
        <SettingsBlock testId="portals-block">
          <BlockMid>
            <div className="flex flex-col gap-(--space-4)">
              {FF_APP_OLX && (
              <OlxConnectionBlock
                settings={settings}
                compact={FF_APP_CANAL_PRO && !!agencyId}
                enabled={settings.enabled_portals.includes('olx')}
                autoPublish={settings.auto_publish_portals.includes('olx')}
                saving={saving}
                onConnect={connectOlx}
                onDisconnect={disconnectOlx}
                onReviewAccount={() => setConfirmOpen(true)}
                onCheckPlan={checkOlxPlan}
                onToggleEnabled={() => toggleEnabled('olx')}
                onToggleAutoPublish={() => toggleAutoPublish('olx')}
                t={t}
              />
              )}
              {FF_APP_CANAL_PRO && agencyId && (
              <CanalProBlock
                settings={settings}
                feedUrl={canalProFeedUrl(window.location.origin, agencyId)}
                saving={saving}
                onToggle={toggleCanalPro}
                {...configProps('canal-pro')}
                t={t}
              />
              )}
              {withCnm && (
              <ChavesNaMaoBlock
                settings={settings}
                saving={saving}
                onToggle={connectCnm}
                onCreateFeedUrl={createCnmFeedUrl}
                onSaveLeadCredentials={saveCnmLeadCredentials}
                agency={{ name: agency?.name || '', document: storedDocument((agency ?? {}) as AgencyDocument) }}
                {...configProps('chaves-na-mao')}
                t={t}
              />
              )}
            </div>
          </BlockMid>
        </SettingsBlock>
      )}

      {FF_APP_OLX && (
        <OlxConfirmAccountModal
          open={confirmOpen && olxAcceptancePending(settings)}
          settings={settings}
          saving={saving}
          onConfirm={() => { setConfirmOpen(false); void confirmOlx(); }}
          onSwitch={() => {
            setConfirmOpen(false);
            void disconnectOlx();
            notifications.show({ title: t('olx.trocarContaTitulo'), message: t('olx.trocarContaDica'), type: 'info' });
          }}
          onClose={() => setConfirmOpen(false)}
          t={t}
        />
      )}



      {feedRows.length > 0 && (
      <SettingsBlock testId="feed-portals-block" title={t('olx.feedsTitulo')} caption={t('olx.feedsDescricao')}>
        <BlockMid>
          <FeedPortalsBlock
            portals={feedRows}
            autoPublishActive={settings.auto_publish_active}
            saving={saving}
            onConnect={setModalOpen}
            onToggleEnabled={toggleEnabled}
            onToggleAutoPublish={toggleAutoPublish}
            t={t}
          />
        </BlockMid>
      </SettingsBlock>
      )}

      {feedRows.map((portal) => (
        <PortalSetupModal
          key={portal.id}
          open={modalOpen === portal.id}
          onClose={() => setModalOpen(null)}
          portalName={portal.label}
          feedUrl={feedUrl(portal.id)}
          agencyName={agency?.name || ''}
          emailContact={FEED_EMAILS[portal.id] ?? ''}
          t={t}
        />
      ))}
    </div>
  );
};
